import { useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { UpIcon } from 'foundations-assets/icons/components/UpIcon';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { OverviewContent } from '../overview/components/OverviewContent';
import SectionHeading from '../../components/SectionHeading';
import InstructorList from '../../components/InstructorList';
import UsageTracker from '../../utils/UsageTracker';
import { ModalTileSection } from '../../components/TrackLessonOverviewModal/subcomponents/ModalTileSection';
import { useFullLiveEvent, useRegisterForSession, useCancelRegistration } from './liveEventQueries';
import { MOCK_INSTRUCTORS } from './liveEventMockData';
import { MYLEARNING_URL_FRAGMENT } from '../../constants/Navigation';
import { LiveEventRegistrationAlert } from './LiveEventRegistrationAlert';
import { SessionCard } from './components/SessionCard';
import SessionDateDisplay from './components/SessionDateDisplay';
import { RegistrationConfirmationModal } from './RegistrationConfirmationModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const INITIAL_SESSIONS_SHOWN = 2;
const VIEW_REGISTERED_EVENTS_TO = `${MYLEARNING_URL_FRAGMENT}?contentType=LIVE_EVENT`;
function CancelRegistrationButton({
  liveEventId,
  sessionId,
  use
}) {
  const {
    cancelRegistration,
    cancellationLoading
  } = useCancelRegistration({
    onError: () => {
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.liveEventOverviewModal.cancelRegistrationError"
        })
      });
    }
  });
  const handleCancel = () => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'live-event-cancel-registration-click',
      contentType: 'LIVE_EVENT',
      contentId: liveEventId,
      sessionId
    });
    void cancelRegistration(sessionId);
  };
  return /*#__PURE__*/_jsx(LoadingButton, {
    loading: cancellationLoading,
    onClick: handleCancel,
    use: use !== null && use !== void 0 ? use : 'tertiary-light',
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.liveEventOverviewModal.cancelRegistration"
    })
  });
}
function RegisterButton({
  liveEventId,
  onRegistered,
  sessionId,
  use
}) {
  const {
    registerForSession,
    registrationLoading
  } = useRegisterForSession({
    onCompleted: onRegistered,
    onError: () => {
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.liveEventOverviewModal.registrationError"
        })
      });
    }
  });
  const handleRegister = () => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'live-event-register-click',
      contentType: 'LIVE_EVENT',
      contentId: liveEventId,
      sessionId
    });
    void registerForSession(sessionId);
  };
  return /*#__PURE__*/_jsx(LoadingButton, {
    loading: registrationLoading,
    onClick: handleRegister,
    use: use !== null && use !== void 0 ? use : 'primary',
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.liveEventOverviewModal.registerForNextWorkshop"
    })
  });
}
function HeroCTA({
  hasLiveSession,
  hasUpcomingSession,
  isLiveSessionRegistered,
  isUpcomingSessionRegistered,
  liveEventId,
  liveSessionId,
  liveSessionJoinUrl,
  nextUpcomingSessionId,
  onRegistered
}) {
  if (isLiveSessionRegistered && liveSessionId !== null) {
    return /*#__PURE__*/_jsx(CancelRegistrationButton, {
      liveEventId: liveEventId,
      sessionId: liveSessionId
    });
  }
  if (hasLiveSession && liveSessionJoinUrl) {
    return /*#__PURE__*/_jsx(Button, {
      external: true,
      href: liveSessionJoinUrl,
      target: "_blank",
      use: "primary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.liveEventOverviewModal.joinSession"
      })
    });
  }
  if (isUpcomingSessionRegistered && nextUpcomingSessionId !== null) {
    return /*#__PURE__*/_jsx(CancelRegistrationButton, {
      liveEventId: liveEventId,
      sessionId: nextUpcomingSessionId
    });
  }
  if (hasUpcomingSession && nextUpcomingSessionId !== null) {
    return /*#__PURE__*/_jsx(RegisterButton, {
      liveEventId: liveEventId,
      onRegistered: onRegistered,
      sessionId: nextUpcomingSessionId
    });
  }
  return null;
}
const LiveEventOverviewModalContainer = ({
  liveEventId,
  language
}) => {
  var _sessions$find, _upcomingSessions$, _upcomingSessions$fin, _assignmentBySessionI3, _liveSession$id, _nextUpcomingSession$;
  const {
    isMobile
  } = useMediaQuery();
  const {
    assignments,
    error,
    liveEvent,
    loading,
    sessions
  } = useFullLiveEvent(liveEventId);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [registeredSessionId, setRegisteredSessionId] = useState(null);
  const registeredSession = (_sessions$find = sessions.find(s => s.id === registeredSessionId)) !== null && _sessions$find !== void 0 ? _sessions$find : null;
  const handleRegistered = data => {
    setRegisteredSessionId(data.registerForSession.sessionId);
  };

  /* This section will change depending on the data from the API */
  const registeredSessionIds = useMemo(() => new Set(assignments.filter(a => a.status === 'registered').map(a => a.sessionId)), [assignments]);
  const assignmentBySessionId = useMemo(() => new Map(assignments.filter(a => a.status === 'registered').map(a => [a.sessionId, a])), [assignments]);
  const liveSession = useMemo(() => {
    var _sessions$find2;
    const now = Date.now();
    return (_sessions$find2 = sessions.find(s => s.startTime <= now && s.endTime > now)) !== null && _sessions$find2 !== void 0 ? _sessions$find2 : null;
  }, [sessions]);
  const upcomingSessions = useMemo(() => {
    const now = Date.now();
    return sessions.filter(s => s.startTime > now).sort((a, b) => a.startTime - b.startTime);
  }, [sessions]);
  const nextUpcomingSession = (_upcomingSessions$ = upcomingSessions[0]) !== null && _upcomingSessions$ !== void 0 ? _upcomingSessions$ : null;
  const remainingUpcomingSessions = upcomingSessions.slice(1);
  const nextRegisteredUpcomingSession = (_upcomingSessions$fin = upcomingSessions.find(s => registeredSessionIds.has(s.id))) !== null && _upcomingSessions$fin !== void 0 ? _upcomingSessions$fin : null;
  const pastRecording = useMemo(() => {
    var _assignmentBySessionI, _assignmentBySessionI2;
    const now = Date.now();
    const pastWithRecording = sessions.filter(s => s.endTime <= now && s.recordingLink && assignmentBySessionId.has(s.id)).sort((a, b) => b.startTime - a.startTime);
    const session = pastWithRecording[0];
    if (!(session !== null && session !== void 0 && session.recordingLink)) {
      return null;
    }
    const attended = (_assignmentBySessionI = (_assignmentBySessionI2 = assignmentBySessionId.get(session.id)) === null || _assignmentBySessionI2 === void 0 ? void 0 : _assignmentBySessionI2.attended) !== null && _assignmentBySessionI !== void 0 ? _assignmentBySessionI : false;
    return {
      attended,
      recordingUrl: session.recordingLink
    };
  }, [assignmentBySessionId, sessions]);
  const isLiveSessionRegistered = liveSession ? registeredSessionIds.has(liveSession.id) : false;
  const liveSessionJoinUrl = liveSession && isLiveSessionRegistered ? ((_assignmentBySessionI3 = assignmentBySessionId.get(liveSession.id)) === null || _assignmentBySessionI3 === void 0 ? void 0 : _assignmentBySessionI3.joinLink) || liveSession.platformLink : null;
  const visibleRemainingSessions = showAllSessions ? remainingUpcomingSessions : remainingUpcomingSessions.slice(0, INITIAL_SESSIONS_SHOWN);
  const getOrganisationUrlClickHandler = (professorId, organisation, organisationUrl) => () => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'professor-organisation-click',
      contentType: 'LIVE_EVENT',
      appearsIn: 'live-event-overview',
      organisation,
      organisationUrl,
      professorId,
      contentId: liveEventId
    });
  };
  return /*#__PURE__*/_jsxs(OverviewContent, {
    assetLanguage: language,
    backgroundColor: "fill-surface-recessed",
    error: error,
    fallbackThumbnailUrl: liveEvent === null || liveEvent === void 0 ? void 0 : liveEvent.previewThumbnailRectangleUrl,
    loading: loading,
    children: [/*#__PURE__*/_jsxs(View, {
      backgroundColor: "fill-surface-recessed",
      paddingInline: isMobile ? '300' : '800',
      children: [/*#__PURE__*/_jsx(LiveEventRegistrationAlert, {
        hasLiveSession: !!liveSession,
        hasRegisteredUpcoming: !!nextRegisteredUpcomingSession,
        isLiveSessionRegistered: isLiveSessionRegistered,
        joinUrl: liveSessionJoinUrl,
        pastRecording: pastRecording,
        viewRegisteredEventsTo: VIEW_REGISTERED_EVENTS_TO
      }), /*#__PURE__*/_jsxs(ModalTileSection, {
        padding: "0",
        paddingBlockStart: "500",
        children: [/*#__PURE__*/_jsx(Text, {
          as: isMobile ? 'h4' : 'h2',
          marginBlockEnd: "400",
          typography: "heading-600",
          children: liveEvent === null || liveEvent === void 0 ? void 0 : liveEvent.title
        }), /*#__PURE__*/_jsx(HeroCTA, {
          hasLiveSession: !!liveSession,
          hasUpcomingSession: !!nextUpcomingSession,
          isLiveSessionRegistered: isLiveSessionRegistered,
          isUpcomingSessionRegistered: !!nextRegisteredUpcomingSession,
          liveEventId: liveEventId,
          liveSessionId: (_liveSession$id = liveSession === null || liveSession === void 0 ? void 0 : liveSession.id) !== null && _liveSession$id !== void 0 ? _liveSession$id : null,
          liveSessionJoinUrl: liveSessionJoinUrl,
          nextUpcomingSessionId: (_nextUpcomingSession$ = nextUpcomingSession === null || nextUpcomingSession === void 0 ? void 0 : nextUpcomingSession.id) !== null && _nextUpcomingSession$ !== void 0 ? _nextUpcomingSession$ : null,
          onRegistered: handleRegistered
        })]
      }), /*#__PURE__*/_jsx(SectionHeading, {
        paddingBlockEnd: "400",
        paddingBlockStart: "800",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.liveEventOverviewModal.nextSession"
        })
      }), nextUpcomingSession ? /*#__PURE__*/_jsx(CardWrapper, {
        marginBlockEnd: "0",
        children: /*#__PURE__*/_jsxs(ModalTileSection, {
          paddingBlockEnd: "600",
          children: [/*#__PURE__*/_jsx(Text, {
            paddingBlockEnd: "200",
            typography: "heading-100",
            children: /*#__PURE__*/_jsx(SessionDateDisplay, {
              endTime: nextUpcomingSession.endTime,
              startTime: nextUpcomingSession.startTime
            })
          }), liveEvent === null || liveEvent === void 0 ? void 0 : liveEvent.description]
        })
      }) : /*#__PURE__*/_jsx(CardWrapper, {
        marginBlockEnd: "0",
        children: /*#__PURE__*/_jsxs(ModalTileSection, {
          paddingBlockEnd: "600",
          children: [/*#__PURE__*/_jsx(Text, {
            paddingBlockEnd: "200",
            typography: "heading-100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.liveEventOverviewModal.noSessionsScheduled"
            })
          }), liveEvent === null || liveEvent === void 0 ? void 0 : liveEvent.description]
        })
      }), remainingUpcomingSessions.length > 0 && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(SectionHeading, {
          paddingBlockEnd: "400",
          paddingBlockStart: "800",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.liveEventOverviewModal.moreUpcomingSessions"
          })
        }), /*#__PURE__*/_jsxs(View, {
          children: [/*#__PURE__*/_jsx(Flex, {
            flexDirection: "column",
            children: visibleRemainingSessions.map(session => /*#__PURE__*/_jsx(SessionCard, {
              isRegistered: registeredSessionIds.has(session.id),
              liveEventId: liveEventId,
              onRegistered: handleRegistered,
              session: session
            }, session.id))
          }), /*#__PURE__*/_jsx(Flex, {
            justifyContent: isMobile ? 'center' : 'start',
            children: remainingUpcomingSessions.length > INITIAL_SESSIONS_SHOWN && /*#__PURE__*/_jsx(Button, {
              onClick: () => setShowAllSessions(prev => !prev),
              responsive: false,
              size: "small",
              use: "transparent",
              children: showAllSessions ? /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.liveEventOverviewModal.showLess"
                }), ' ', /*#__PURE__*/_jsx(UpIcon, {})]
              }) : /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.liveEventOverviewModal.showMore"
                }), ' ', /*#__PURE__*/_jsx(DownIcon, {})]
              })
            })
          })]
        })]
      }), MOCK_INSTRUCTORS.length > 0 && /*#__PURE__*/_jsx(InstructorList, {
        handleOrganisationUrlClick: getOrganisationUrlClickHandler,
        language: language.toLowerCase(),
        professors: MOCK_INSTRUCTORS
      }), /*#__PURE__*/_jsx(View, {
        paddingBlockEnd: "800"
      })]
    }), registeredSession && liveEvent && /*#__PURE__*/_jsx(RegistrationConfirmationModal, {
      liveEventDescription: liveEvent.description,
      liveEventTitle: liveEvent.title,
      onClose: () => setRegisteredSessionId(null),
      session: registeredSession
    })]
  });
};
export default LiveEventOverviewModalContainer;